import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { get, findIndex, map, includes, size } from "lodash";
import { useGoogleLogin } from "@react-oauth/google";

import { authHelpers, generalHelpers, indexDbHelpers, billingAccessHelpers } from "../../helpers";
import Icons from "../../components/icons";
import { ICONS } from "../../constants/icons.constants";
import { Button, Input } from "../../kaya";
import GoogleButton from "../../components/GoogleButton/GoogleButton";
import { routesConstants } from "../../constants";
import userStore from "../../zustand/user.store";
import playlistStore from "../../zustand/playlist.store";
import likedSongsStore from "../../zustand/likedSong.store";
import SchedulesStore from "../../zustand/Schedule.store";
import musicPlayerStore from "../../zustand/musicPlayer.store";
import ignoredSongStore from "../../zustand/ignoreSong.store";
import favoriteSongStore from "../../zustand/favoriteSong.store";
import disLikedSongsStore from "../../zustand/dislikedSong.store";
import { userActions } from "../../actions";
import { appActions } from "../../actions";
import {
  getAuthFromIndexDB,
  getUserFromIndexDB,
  storeUserInIndexDB,
} from "../../helpers/indexDb.helpers";

import logo from "../../assets/Logo.png";

import styles from "./login.module.scss";

const findFirstUnIngoredSong = (songs, ignoredSongs) => {
  const ingoreSongIds = map(ignoredSongs, (i) => i.id);
  let index = 0;
  for (let i = 0; i < size(songs); i++) {
    if (!includes(ingoreSongIds, songs[i].id)) {
      index = i;
      break;
    }
  }
  return index;
};

const Login = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // const [error, setError] = useState({});
  const [apiError, setApiEror] = useState("");

  const {
    setUser,
    defaultUser,
    setAuthentication,
    userOffline,
    user,
    setUserOffline,
  } = userStore((state) => ({
    user: state.user,
    setUser: state.setUser,
    defaultUser: state.defaultUser,
    setAuthentication: state.setAuthentication,
    userOffline: state.userOffline,
    setUserOffline: state.setUserOffline,
  }));

  const { setPlaylists } = playlistStore((state) => ({
    setPlaylists: state.setPlaylists,
  }));
  const { setSongs: setLikedSongs } = likedSongsStore((state) => {
    return {
      setSongs: state.setSongs,
    };
  });
  const { setSongs: setDislikeSongs } = disLikedSongsStore((state) => {
    return {
      setSongs: state.setSongs,
    };
  });
  const { setSongs: setFavouriteSongs } = favoriteSongStore((state) => {
    return {
      setSongs: state.setSongs,
    };
  });
  const { setSongs: setIgnoredSongs } = ignoredSongStore((state) => {
    return {
      setSongs: state.setSongs,
    };
  });
  const { setSchedules } = SchedulesStore((state) => ({
    setSchedules: state.setSchedules,
  }));
  const { setSongs, setPlayList, toggleIsShuffle } = musicPlayerStore(
    (state) => {
      return {
        setSongs: state.setSongs,
        setPlayList: state.setPlayList,
        toggleIsShuffle: state.toggleIsShuffle,
      };
    }
  );

  const handleEyeClick = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async () => {
    const error = {
      username: authHelpers.validateUsernameOrEmail(username),
      password: generalHelpers.validatePassword(password),
    };

    if (!generalHelpers.isEmptyError(error)) {
      setErr(error);
      return;
    }
    // write api call here
    setLoading(true);

    const data = await userActions.loginAction(
      {
        email: username,
        password,
      },
      userOffline
    );
    if (!data.success) {
      setApiEror("Invalid username or password");
      setLoading(false);
      return;
    }

    // New backend response includes user object and routing flags
    const responseData = get(data, "data", {});
    const userFromResponse = get(responseData, "user", null);
    const onboardingStep = get(responseData, "onboarding_step", undefined);
    const canAccessPlayer = get(responseData, "can_access_player", undefined);

    // Use new response format if available
    if (userFromResponse && onboardingStep !== undefined) {
      // Check if user is blocked
      if (get(userFromResponse, "status", "") === "BLOCKED") {
        setAuthentication({
          isAuthenticated: false,
          isPhoneVerified: false,
          isBusinessVerified: false,
          isPrimium: false,
        });
        generalHelpers.toaster("error", "You are Blocked by admin");
        setLoading(false);
        return;
      }

      // Get auth state and route from helper
      const authState = billingAccessHelpers.getAuthStateFromResponse(responseData);
      const { route, openTab } = billingAccessHelpers.getPostLoginRoute(responseData);

      // If can access player, load full app data
      if (canAccessPlayer) {
        const userId = get(userFromResponse, "id", "");
        const {
          user: freshUser,
          playlists,
          status,
          likedSongs,
          ignoredSongs,
          favoritePlaylist,
          favouriteSongs,
          dislikedSongs,
          schedules,
        } = await appActions.fetchAppData(userId, userOffline);

        const index = findFirstUnIngoredSong(favoritePlaylist?.songs, ignoredSongs);
        await storeUserInIndexDB(freshUser, "user-data");

        setUser(freshUser);
        setPlaylists(playlists);
        setLikedSongs(likedSongs);
        setDislikeSongs(dislikedSongs);
        setFavouriteSongs(favouriteSongs);
        setIgnoredSongs(ignoredSongs);
        setSchedules(schedules);
        setSongs([]);
        setPlayList(favoritePlaylist, index);
        if (status?.last_shuffled !== undefined) {
          toggleIsShuffle(status.last_shuffled);
        }
      } else {
        // Just set the user from login response
        setUser(userFromResponse);
      }

      setAuthentication({
        isAuthenticated: authState.isAuthenticated,
        isPhoneVerified: authState.isPhoneVerified,
        isBusinessVerified: authState.isBusinessVerified,
        isPrimium: authState.isPrimium,
      });

      setLoading(false);

      if (route) {
        if (openTab) {
          navigate(route, { state: { openTab } });
        } else {
          navigate(route);
        }
      }
      return;
    }

    // Fallback: Old response format (backward compatibility)
    setUser({
      ...defaultUser,
      id: get(data, "data.id", {}),
    });

    const userData = await userActions.fetchUserAction(
      get(data, "data.id", "")
    );

    if (userData.data.code === "PHONE_NOT_VERIFIED") {
      setAuthentication({
        isAuthenticated: true,
        isPhoneVerified: false,
        isBusinessVerified: false,
        isPrimium: false,
      });
      navigate(routesConstants.ROUTES.PERSONAL_INFO);
    } else if (userData.data.code === "BUSINESS_NOT_FOUND") {
      setAuthentication({
        isAuthenticated: true,
        isPhoneVerified: true,
        isBusinessVerified: false,
        isPrimium: false,
      });
      navigate(routesConstants.ROUTES.BUSINESS_INFO);
    } else if (userData.data.code === "PRIMIUM_NOT_FOUND") {
      setAuthentication({
        isAuthenticated: true,
        isPhoneVerified: true,
        isBusinessVerified: true,
        isPrimium: false,
      });

      navigate(routesConstants.ROUTES.PACKAGES);
    } else if (userData.data.code === "USER_BLOCKED") {
      setAuthentication({
        isAuthenticated: false,
        isPhoneVerified: false,
        isBusinessVerified: false,
        isPrimium: false,
      });

      generalHelpers.toaster("error", "You are Blocked by admin");
      setLoading(false);
      return;
    } else if (userData.data.code === "OK") {
      const {
        user,
        playlists,
        status,
        likedSongs,
        schedules,
        songs,
        ignoredSongs,
        favoritePlaylist,
        favouriteSongs,
        dislikedSongs,
      } = await appActions.fetchAppData(get(data, "data.id", ""), userOffline);

      const index = findFirstUnIngoredSong(
        favoritePlaylist?.songs,
        ignoredSongs
      );
      await storeUserInIndexDB(user, "user-data");

      setUser(user);

      setPlaylists(playlists);
      setLikedSongs(likedSongs);
      setDislikeSongs(dislikedSongs);
      setFavouriteSongs(favouriteSongs);
      setSchedules(schedules);
      setSongs(songs);
      setIgnoredSongs(ignoredSongs);
      setPlayList(favoritePlaylist, index);
      toggleIsShuffle(status.last_shuffled);

      setAuthentication({
        isAuthenticated: true,
        isPhoneVerified: true,
        isBusinessVerified: true,
        isPrimium: true,
      });

      setLoading(false);

      navigate(routesConstants.ROUTES.HOME);
    } else {
      setAuthentication({
        isAuthenticated: false,
        isPhoneVerified: false,
        isBusinessVerified: false,
        isPrimium: false,
      });
      setApiEror("Something went wrong");
      setLoading(false);
    }

    setLoading(false);
  };

  const onSuccess = async (res) => {
    try {
      setLoading2(true);
      const { code } = res;

      const data = await userActions.googleLoginAction(code);

      if (!data.success) {
        setLoading2(false);
        return;
      }

      // New backend response includes user object and routing flags
      const responseData = get(data, "data", {});
      const userFromResponse = get(responseData, "user", null);
      const onboardingStep = get(responseData, "onboarding_step", undefined);
      const canAccessPlayer = get(responseData, "can_access_player", undefined);

      // Use new response format if available
      if (userFromResponse && onboardingStep !== undefined) {
        // Check if user is blocked
        if (get(userFromResponse, "status", "") === "BLOCKED") {
          setAuthentication({
            isAuthenticated: false,
            isPhoneVerified: false,
            isBusinessVerified: false,
            isPrimium: false,
          });
          generalHelpers.toaster("error", "You are Blocked by admin");
          setLoading2(false);
          return;
        }

        // Get auth state and route from helper
        const authState = billingAccessHelpers.getAuthStateFromResponse(responseData);
        const { route, openTab } = billingAccessHelpers.getPostLoginRoute(responseData);

        // If can access player, load full app data
        if (canAccessPlayer) {
          const userId = get(userFromResponse, "id", "");
          const {
            user: freshUser,
            playlists,
            status,
            likedSongs,
            ignoredSongs,
            favoritePlaylist,
            favouriteSongs,
            dislikedSongs,
            schedules,
          } = await appActions.fetchAppData(userId, userOffline);

          const index = findFirstUnIngoredSong(favoritePlaylist?.songs, ignoredSongs);
          await storeUserInIndexDB(freshUser, "user-data");

          setUser(freshUser);
          setPlaylists(playlists);
          setLikedSongs(likedSongs);
          setDislikeSongs(dislikedSongs);
          setFavouriteSongs(favouriteSongs);
          setIgnoredSongs(ignoredSongs);
          setSchedules(schedules);
          setSongs([]);
          setPlayList(favoritePlaylist, index);
          if (status?.last_shuffled !== undefined) {
            toggleIsShuffle(status.last_shuffled);
          }
        } else {
          // Just set the user from login response
          setUser(userFromResponse);
        }

        setAuthentication({
          isAuthenticated: authState.isAuthenticated,
          isPhoneVerified: authState.isPhoneVerified,
          isBusinessVerified: authState.isBusinessVerified,
          isPrimium: authState.isPrimium,
        });

        setLoading2(false);

        if (route) {
          if (openTab) {
            navigate(route, { state: { openTab } });
          } else {
            navigate(route);
          }
        }
        return;
      }

      // Fallback: Old response format (backward compatibility)
      setUser({
        ...defaultUser,
        id: get(data, "data.id", {}),
      });

      const userData = await userActions.fetchUserAction(
        get(data, "data.id", "")
      );

      if (userData.data.code === "PHONE_NOT_VERIFIED") {
        setAuthentication({
          isAuthenticated: true,
          isPhoneVerified: false,
          isBusinessVerified: false,
          isPrimium: false,
        });
        navigate(routesConstants.ROUTES.PERSONAL_INFO);
      } else if (userData.data.code === "BUSINESS_NOT_FOUND") {
        setAuthentication({
          isAuthenticated: true,
          isPhoneVerified: true,
          isBusinessVerified: false,
          isPrimium: false,
        });
        navigate(routesConstants.ROUTES.BUSINESS_INFO);
      } else if (userData.data.code === "PRIMIUM_NOT_FOUND") {
        setAuthentication({
          isAuthenticated: true,
          isPhoneVerified: true,
          isBusinessVerified: true,
          isPrimium: false,
        });

        navigate(routesConstants.ROUTES.PACKAGES);
      } else if (userData.data.code === "USER_BLOCKED") {
        setAuthentication({
          isAuthenticated: false,
          isPhoneVerified: false,
          isBusinessVerified: false,
          isPrimium: false,
        });

        generalHelpers.toaster("error", "You are Blocked by admin");
        setLoading2(false);
        return;
      } else if (userData.data.code === "OK") {
        setUser(get(userData, "data.data", {}));

        const {
          user,
          playlists,
          status,
          likedSongs,
          schedules,
          songs,
          ignoredSongs,
          favoritePlaylist,
          favouriteSongs,
          dislikedSongs,
        } = await appActions.fetchAppData(
          get(data, "data.id", ""),
          userOffline
        );

        const index = findFirstUnIngoredSong(
          favoritePlaylist?.songs,
          ignoredSongs
        );
        setUser(user);
        await storeUserInIndexDB(user, "user-data");

        setPlaylists(playlists);
        setLikedSongs(likedSongs);
        setDislikeSongs(dislikedSongs);
        setFavouriteSongs(favouriteSongs);
        setSchedules(schedules);
        setSongs(songs);
        setIgnoredSongs(ignoredSongs);
        setPlayList(favoritePlaylist, index);
        toggleIsShuffle(status.last_shuffled);

        setAuthentication({
          isAuthenticated: true,
          isPhoneVerified: true,
          isBusinessVerified: true,
          isPrimium: true,
        });

        setLoading2(false);

        navigate(routesConstants.ROUTES.HOME);
      } else {
        setAuthentication({
          isAuthenticated: false,
          isPhoneVerified: false,
          isBusinessVerified: false,
          isPrimium: false,
        });
        setApiEror("Something went wrong");

        setLoading2(false);
      }
    } catch (error) {
      generalHelpers.toaster("error", error.message || "Something went wrong");
    } finally {
      setLoading2(false);
    }
  };

  const onError = (err) => {
    const { error_description } = err;
    generalHelpers.toaster(
      "error",
      error_description || "Something went wrong"
    );
  };

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess,
    onError,
    //scope: only
  });

  const handleSignUp = () => {
    navigate(routesConstants.ROUTES.REGISTER);
  };

  const handleForgotPassword = () => {
    navigate(routesConstants.ROUTES.FORGOT_PASSWORD);
  };

  useEffect(() => {
    window.addEventListener("load", async () => {
      if (navigator.onLine) {
        setUserOffline(false);
      } else {
        setUserOffline(true);

        const userfromDb = await getUserFromIndexDB("user-data");
        const authData = await getAuthFromIndexDB("authentication-data");
        const offlinePlaylist = await indexDbHelpers.getPlaylistsFromDB();

        setPlaylists(offlinePlaylist);
        setUser(userfromDb.user);
        setAuthentication(authData.auth);
      }
    });
    window.addEventListener("online", () => {
      setUserOffline(false);
    });
    window.addEventListener("offline", async () => {
      setUserOffline(true);

      const offlinePlaylist = await indexDbHelpers.getPlaylistsFromDB();

      setPlaylists(offlinePlaylist);

      const userfromDb = await getUserFromIndexDB("user-data");
      const authData = await getAuthFromIndexDB("authentication-data");
      setUser(userfromDb.user);
      setAuthentication(authData.auth);
    });
  }, [setAuthentication, setPlaylists, setUser, setUserOffline, user]);

  return (
    <div className={styles.Login}>
      <div className={styles.logo}>
        {/* <Icons type={ICONS.LOGO} /> */}
        <img src={logo} alt="" />
      </div>
      <div className={styles.boxLogin}>
        <h1 className={styles.title}>Login</h1>
        <p className={styles.title2}>Welcome to For Play</p>
        <form className={styles.form}>
          <div className={styles.box}>
            <Input
              type="string"
              placeholder="Username or Email"
              containerClassName={styles.input1}
              onChange={(e) => {
                setUsername(e);
                setErr({
                  ...err,
                  ["username"]: "",
                });
              }}
              errors={err}
              id="username"
            />
            <Input
              type={showPassword ? "string" : "password"}
              placeholder="Password"
              containerClassName={styles.input2}
              onChange={(e) => {
                setPassword(e);
                setErr({
                  ...err,

                  ["password"]: "",
                });
              }}
              errors={err}
              id="password"
              onEnterClick={handleLogin}
              suffixIcon={
                <Icons type={showPassword ? ICONS.EYE_OFF : ICONS.EYE_ON} />
              }
              onSuffixIconClick={handleEyeClick}
            />
            <p className={styles.text} onClick={handleForgotPassword}>
              Forgot Password?
            </p>

            <Button
              type="Primary"
              className={styles.btn1}
              onClick={handleLogin}
              isLoading={loading}
            >
              Login
            </Button>

            <GoogleButton
              onClick={googleLogin}
              disabled={loading2}
              className={styles.googleBtn}
            />
            <div className={styles.account}>
              <div className={styles.line1}></div>
              <p className={styles.text2}>Don’t have an account?</p>
              <div className={styles.line2}></div>
            </div>
            <Button
              className={styles.btn3}
              type="Outline"
              onClick={handleSignUp}
            >
              Sign Up
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
